import React from 'react';

import {useOverrideStatuses} from '../../hooks/useLibOverrides';
import {clearOverrides} from '../../utils/libVersionManager';
import type {OverrideStatus} from '../../utils/libVersionManager';

const suffixOf = (status: OverrideStatus): string => (status.implicit ? ' (auto)' : '');

const describeFailure = (status: OverrideStatus): string => {
  const reason = status.reason ?? 'unknown error';
  return `${status.lib}@${status.requested}${suffixOf(status)}: FAILED — built-in running (${reason})`;
};

const describeStatus = (status: OverrideStatus): string => {
  if (status.state === 'loading') return `${status.lib}@${status.requested}${suffixOf(status)}: loading…`;
  if (status.state === 'failed') return describeFailure(status);
  return `${status.lib}@${status.loadedVersion}${suffixOf(status)}`;
};

const badgeColor = (statuses: OverrideStatus[]): string => {
  if (statuses.some((status) => status.state === 'failed')) return 'bg-red-600 text-white';
  if (statuses.some((status) => status.state === 'loading')) return 'bg-gray-400 text-black';
  return 'bg-amber-400 text-black';
};

/**
 * Floating badge shown on every route while library version overrides are
 * requested. Reports the truth: the version the loaded bundle
 * itself claims (its getVersion()), a spinner while loading, and a loud
 * failure state when the built-in version is running instead.
 */
const LibVersionBadge = () => {
  const statuses = useOverrideStatuses();

  if (statuses.length === 0) return null;

  const badgeClasses = [
    'fixed bottom-3 right-3 z-[9999] flex items-center gap-2',
    'rounded-md px-3 py-1.5 font-mono text-xs shadow-lg',
    badgeColor(statuses),
  ].join(' ');
  const hasFailed = statuses.some((status) => status.state === 'failed');

  return (
    <div
      aria-live={hasFailed ? 'assertive' : 'polite'}
      className={badgeClasses}
      data-testid="lib-version-badge"
      role="status"
    >
      <div className="flex flex-col gap-0.5">
        {statuses.map((status) => (
          <span key={status.lib}>{describeStatus(status)}</span>
        ))}
      </div>
      <button
        className="rounded border border-current px-1.5 py-0.5 text-[11px]"
        onClick={clearOverrides}
        title="Clear version overrides and reload"
        type="button"
      >
        Reset
      </button>
    </div>
  );
};

export default LibVersionBadge;
